import { partition } from 'lodash-es';
import { useMemo } from 'react';

import type {
  AssetMetadataV2Lookup,
  ChainAddressId,
  SupportLevel,
  SupportLevelsByChainAddressId,
} from '@infinex/asset-config';
import {
  chainAddressIdToObject,
  checkChainAddressSupportLevel,
  getSupportLevelsByChainAddressIdFromAssetMetadataBatch,
} from '@infinex/asset-config';
import {
  type ChainKey,
  chains,
  getNearIntentsRuntimeFromAssetId,
} from '@infinex/chain-config';
import type { Custody, LoadingState } from '@infinex/types';
import { useActiveFrontendChainKeys } from '@infinex/user';

import { useAssetMetadataV2BatchQuery } from './useAssetMetadataV2BatchQuery';

import type {
  NullableChainRuntime,
  NullableNearIntentsRuntime,
} from '../../../api/chains';
import type {
  BalancesByChainAddressId,
  ChainNftAssetBalanceSummed,
  ChainPositionChainAssetBalanceSummed,
} from '../../../features/balances';
import { useUnifiedBalances } from '../../../features/balances';

interface AssetFilters {
  chain?: ChainKey;
  chainRuntime?: NullableChainRuntime;
  nearIntentsRuntime?: NullableNearIntentsRuntime;
  minimumSupportLevel?: SupportLevel;
  search?: string;
  custody?: Custody;
  walletIds?: string[];
}

export type FilteredAssetResult = {
  filteredTokens: BalancesByChainAddressId;
  filteredNfts: ChainNftAssetBalanceSummed[];
  /** Collectible positions (e.g. Collector Crypt cards) matching the filters. */
  filteredCollectibles: ChainPositionChainAssetBalanceSummed[];
  patronNfts?: ChainNftAssetBalanceSummed;
  hasAnyAssets: boolean; // True if the user has any assets at all over zero balances
  hasFilteredAssets: boolean; // True if the user has any assets over zero balances that match the filters provided
};

/**
 * A hook that filters and organizes user balances (tokens, NFTs, and patron NFTs) based on specified criteria.
 * Used primarily in move / send asset selection interfaces.
 *
 * @param filters - Configuration object for filtering assets
 * @param filters.chain - Filter assets by specific network (e.g., 'ethereum', 'arbitrum', 'solana')
 * @param filters.chainRuntime - Filter assets by specific chain runtime (e.g., 'evm', 'svm', 'nearIntents')
 * @param filters.search - Filter assets by name, symbol, or ID (case-insensitive)
 * @param filters.custody - Filter assets by custody type (e.g., 'custody', 'non-custody')
 *
 * @returns {FilteredAssetResult} Object containing filtered assets and status flags
 */
export function useBalanceFiltering({
  custody,
  chain,
  chainRuntime,
  nearIntentsRuntime,
  minimumSupportLevel = 'full',
  search = '',
  walletIds,
}: AssetFilters): LoadingState<FilteredAssetResult, FilteredAssetResult> {
  // maybe use?
  const activeChains = useActiveFrontendChainKeys({ custody });

  const {
    balancesByChainAddressId,
    collectibleBalances,
    withdrawablePatronNfts,
    isLoading: isBalancesLoading,
  } = useUnifiedBalances({
    custody: custody ? [custody] : undefined,
    walletIds,
    chain: chain ? [chain] : undefined,
  });

  const balanceChainAddressLookups = useMemo<AssetMetadataV2Lookup[]>(
    () =>
      Object.keys(balancesByChainAddressId ?? {}).map((chainAddressId) =>
        chainAddressIdToObject(chainAddressId as ChainAddressId)
      ),
    [balancesByChainAddressId]
  );

  const { data: metadataByCacheKey, isPending: isMetadataPending } =
    useAssetMetadataV2BatchQuery(balanceChainAddressLookups);

  // `useAssetMetadataV2BatchQuery` is disabled for empty lookups, in which case
  // `isPending` stays true indefinitely; treat that as "not loading". When the
  // hook is enabled and we have no data yet, we must gate filtering — otherwise
  // the support map is `{}` and `checkChainAddressSupportLevel` falls back to
  // 'full', briefly admitting assets that should be filtered out by
  // `minimumSupportLevel`.
  const isMetadataLoading =
    isMetadataPending && balanceChainAddressLookups.length > 0;

  const supportLevelsByChainAddressId = useMemo(
    () =>
      getSupportLevelsByChainAddressIdFromAssetMetadataBatch(
        metadataByCacheKey
      ),
    [metadataByCacheKey]
  );

  return useMemo(() => {
    return filterUserBalance({
      isBalancesLoading: isBalancesLoading || isMetadataLoading,
      balancesByChainAddressId: balancesByChainAddressId ?? {},
      collectibleBalances: collectibleBalances ?? [],
      minimumSupportLevel: minimumSupportLevel,
      supportLevelsByChainAddressId: supportLevelsByChainAddressId,
      chainRuntime: chainRuntime ?? null,
      nearIntentsRuntime: nearIntentsRuntime ?? null,
      activeChains: activeChains,
      search: search,
      withdrawablePatronNfts: withdrawablePatronNfts ?? undefined,
    });
  }, [
    isBalancesLoading,
    isMetadataLoading,
    balancesByChainAddressId,
    collectibleBalances,
    minimumSupportLevel,
    supportLevelsByChainAddressId,
    chainRuntime,
    activeChains,
    search,
    withdrawablePatronNfts,
    nearIntentsRuntime,
  ]);
}

function filterUserBalance({
  isBalancesLoading,
  balancesByChainAddressId,
  collectibleBalances,
  minimumSupportLevel,
  supportLevelsByChainAddressId,
  chainRuntime,
  nearIntentsRuntime,
  activeChains,
  search,
  withdrawablePatronNfts,
}: {
  isBalancesLoading: boolean;
  balancesByChainAddressId: BalancesByChainAddressId;
  collectibleBalances: ChainPositionChainAssetBalanceSummed[];
  minimumSupportLevel: SupportLevel;
  supportLevelsByChainAddressId: SupportLevelsByChainAddressId;
  chainRuntime: NullableChainRuntime;
  nearIntentsRuntime: NullableNearIntentsRuntime;
  activeChains: ChainKey[];
  search: string;
  withdrawablePatronNfts: ChainNftAssetBalanceSummed | undefined;
}) {
  if (isBalancesLoading)
    return {
      isLoading: true,
      value: {
        filteredTokens: {},
        filteredNfts: [],
        filteredCollectibles: [],
        patronNfts: undefined,
        hasAnyAssets: false,
        hasFilteredAssets: false,
      },
    };

  // All feature flagged chains are covered by the balance call (near intents, sonic)
  // All chain filteres are also covered by unified balances

  //Step 1: Filter by minimum support level
  const minimumSupportLevelFiltered = filterByMinimumSupportLevel(
    balancesByChainAddressId,
    minimumSupportLevel,
    supportLevelsByChainAddressId
  );

  // Step 2: Apply chain runtime filtering
  const runtimeFiltered = filterByChainRuntime(
    minimumSupportLevelFiltered,
    chainRuntime ?? null,
    nearIntentsRuntime ?? null,
    activeChains
  );

  // Step 3: Apply search filtering
  const searchFiltered = filterBySearch(runtimeFiltered, search);

  // Collectibles bypass the asset-metadata support gate (they are intentionally
  // not in the official list); only the chain-runtime and search filters apply.
  // NB: collectibles are deliberately NOT folded into hasAnyAssets/hasFilteredAssets
  // — those flags drive empty-state logic in surfaces that can't transfer
  // collectibles (swidge, perps, gas). The send/move picker, the only surface that
  // renders collectibles, accounts for them in its own empty-state check.
  const filteredCollectibles = filterCollectibles(
    collectibleBalances,
    chainRuntime,
    activeChains,
    search
  );

  const hasAnyAssets = Boolean(Object.keys(searchFiltered).length > 0);

  const hasFilteredAssets = Boolean(Object.keys(searchFiltered).length > 0);

  const [allFilteredNftsEntries, filteredTokensEntries] = partition(
    Object.entries(searchFiltered),
    ([_, balance]) => balance?.type === 'nft'
  );

  const [[patronNftEntry], filteredNftsEntries] = partition(
    allFilteredNftsEntries,
    ([_, balance]) => balance?.assetId === 'patron_nft'
  );

  // use the patronNftEntry here to determine if it passed all search and other filters
  // but return only the withdrawable ones
  const patronNfts = patronNftEntry
    ? (withdrawablePatronNfts ?? undefined)
    : undefined;

  return {
    isLoading: false,
    value: {
      filteredTokens: Object.fromEntries(
        filteredTokensEntries
      ) as BalancesByChainAddressId,
      filteredNfts: filteredNftsEntries.map(
        ([_, balance]) => balance
      ) as ChainNftAssetBalanceSummed[],
      filteredCollectibles,
      patronNfts,
      hasAnyAssets,
      hasFilteredAssets,
    },
  };
}

/**
 * Filters collectible positions by chain runtime and search. Collectibles are
 * SVM-only (e.g. Collector Crypt on Solana) and are never near-intents assets,
 * so a near-intents runtime filter excludes them entirely.
 */
const filterCollectibles = (
  collectibles: ChainPositionChainAssetBalanceSummed[],
  chainRuntime: NullableChainRuntime,
  activeChains: ChainKey[],
  search: string
): ChainPositionChainAssetBalanceSummed[] => {
  const lowerQuery = search.toLowerCase();
  return collectibles.filter((collectible) => {
    if (chainRuntime) {
      if (!activeChains.includes(collectible.chain)) return false;
      if (chainRuntime !== chains[collectible.chain].runtime) return false;
    }
    if (lowerQuery) {
      const searchString =
        `${collectible.symbol}${collectible.name}${collectible.chain}`.toLowerCase();
      if (!searchString.includes(lowerQuery)) return false;
    }
    return true;
  });
};

/**
 * Filters assets based on the specified minimum support level.
 */
const filterByMinimumSupportLevel = (
  balances: BalancesByChainAddressId,
  minimumSupportLevel: SupportLevel,
  chainAddressSupportLevels: SupportLevelsByChainAddressId
): BalancesByChainAddressId => {
  return Object.fromEntries(
    Object.entries(balances).filter(([chainAddressId]) =>
      checkChainAddressSupportLevel({
        chainAddressSupportLevels: chainAddressSupportLevels,
        supportLevel: minimumSupportLevel,
        chainAddressId: chainAddressId as ChainAddressId,
      })
    )
  );
};
/**
 * Filters assets based on the specified chain runtime.
 * If no chain runtime is specified, returns all assets.
 */
const filterByChainRuntime = (
  balances: BalancesByChainAddressId,
  chainRuntime: NullableChainRuntime,
  nearIntentsRuntime: NullableNearIntentsRuntime,
  activeChains: ChainKey[]
): BalancesByChainAddressId => {
  if (!chainRuntime) return balances;
  return Object.fromEntries(
    Object.entries(balances).filter(
      ([_, balance]) =>
        balance &&
        activeChains.includes(balance.chain) &&
        chainRuntime === chains[balance.chain].runtime &&
        (chainRuntime !== 'nearIntents' ||
          nearIntentsRuntime ===
            getNearIntentsRuntimeFromAssetId(balance.assetId))
    )
  );
};

// This may become a poor implementation when the data set becomes large enough but good for now
const filterBySearch = (
  assets: BalancesByChainAddressId,
  search: string
): BalancesByChainAddressId => {
  if (!search) return assets;
  const lowerQuery = search.toLowerCase();
  return Object.entries(assets).reduce((acc, [chainAddressId, balance]) => {
    if (!balance) return acc;

    const searchString =
      `${balance.symbol}${balance.name}${balance.assetId}${balance.chain}`.toLowerCase();

    if (searchString.includes(lowerQuery)) {
      acc[chainAddressId as ChainAddressId] = balance;
    }
    return acc;
  }, {} as BalancesByChainAddressId);
};
